import React, { useState } from 'react';
import { Button, Input, Select, DatePicker, Tooltip } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import dayjs, { Dayjs } from 'dayjs';
import { METER_BY_NUMBER } from 'graphql/meterCoreInfo.gql';
import styles from './Audit.module.css';

const { RangePicker } = DatePicker;

export type DatePreset = 'today' | 'last7' | 'thisMonth' | 'custom';
export type AuditSearchMode = 'meter' | 'vendor';

export interface AuditFilters {
  startDate: string;
  endDate: string;
  meterId?: number | null;
  vendorName?: string | null;
  suppressQuery?: boolean;
  actorFilter?: string;
  windFarmFilter?: string;
}

interface Props {
  recordCount: number;
  onChange: (filters: AuditFilters) => void;
}

const PRESET_LABELS: { key: DatePreset; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'last7', label: 'Last 7 Days' },
  { key: 'thisMonth', label: 'This Month' },
  { key: 'custom', label: 'Custom Range' },
];

const SEARCH_MODE_OPTIONS: { value: AuditSearchMode; label: string }[] = [
  { value: 'meter', label: 'Meter' },
  { value: 'vendor', label: 'Vendor' },
];

export function getDateRange(preset: DatePreset, custom: [Dayjs, Dayjs] | null): { startDate: string; endDate: string } {
  const now = dayjs();
  if (preset === 'today') {
    return {
      startDate: now.startOf('day').toISOString(),
      endDate: now.add(1, 'day').startOf('day').toISOString(),
    };
  }
  if (preset === 'last7') {
    return {
      startDate: now.subtract(6, 'day').startOf('day').toISOString(),
      endDate: now.add(1, 'day').startOf('day').toISOString(),
    };
  }
  if (preset === 'thisMonth') {
    return {
      startDate: now.startOf('month').toISOString(),
      endDate: now.add(1, 'month').startOf('month').toISOString(),
    };
  }
  if (preset === 'custom' && custom) {
    return {
      startDate: custom[0].startOf('day').toISOString(),
      endDate: custom[1].add(1, 'day').startOf('day').toISOString(),
    };
  }
  return { startDate: '', endDate: '' };
}

function presetTooltip(key: DatePreset): string {
  if (key === 'custom') return 'Shows changes recorded within your selected date range.';
  const { startDate, endDate } = getDateRange(key, null);
  const fmt = (iso: string) => dayjs(iso).format('MMM D, YYYY');
  const end = dayjs(endDate).subtract(1, 'day');
  const range = dayjs(startDate).isSame(end, 'day') ? fmt(startDate) : `${fmt(startDate)} – ${end.format('MMM D, YYYY')}`;
  const prefix: Record<Exclude<DatePreset, 'custom'>, string> = {
    today:     'Shows changes recorded today',
    last7:     'Shows changes recorded in the last 7 days',
    thisMonth: 'Shows changes recorded this month',
  };
  return `${prefix[key as Exclude<DatePreset, 'custom'>]} (${range}).`;
}

const AuditFilterPanel = ({ recordCount, onChange }: Props) => {
  const [preset, setPreset] = useState<DatePreset>('thisMonth');
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [showRangePicker, setShowRangePicker] = useState(false);

  const [searchMode, setSearchMode] = useState<AuditSearchMode>('meter');

  // Meter search state
  const [meterInput, setMeterInput] = useState('');
  const [resolvedMeter, setResolvedMeter] = useState<{ meterId: number; meterName: string } | null>(null);
  const [meterNotFound, setMeterNotFound] = useState(false);
  const [committedMeterId, setCommittedMeterId] = useState<number | null>(null);

  // Vendor search state (partial match — no resolve-before-search step)
  const [vendorInput, setVendorInput] = useState('');
  const [committedVendorName, setCommittedVendorName] = useState<string | null>(null);

  const [lookupMeter, { loading: meterLoading }] = useLazyQuery(METER_BY_NUMBER, {
    fetchPolicy: 'cache-first',
    onCompleted(data) {
      if (data?.meterByNumber) {
        setResolvedMeter({ meterId: data.meterByNumber.meterId, meterName: data.meterByNumber.meterName });
        setMeterNotFound(false);
      } else {
        setResolvedMeter(null);
        setMeterNotFound(true);
      }
    },
  });

  const emit = (
    nextPreset: DatePreset,
    nextCustom: [Dayjs, Dayjs] | null,
    nextMeterId: number | null,
    nextVendorName: string | null,
    suppress = false,
  ) => {
    const { startDate, endDate } = getDateRange(nextPreset, nextCustom);
    onChange({ startDate, endDate, meterId: nextMeterId, vendorName: nextVendorName, suppressQuery: suppress });
  };

  const handlePreset = (key: DatePreset) => {
    setPreset(key);
    const meterId = searchMode === 'meter' ? committedMeterId : null;
    const vendorName = searchMode === 'vendor' ? committedVendorName : null;
    if (key === 'custom') {
      setShowRangePicker(true);
      emit(key, customRange, meterId, vendorName);
    } else {
      setShowRangePicker(false);
      setCustomRange(null);
      emit(key, null, meterId, vendorName);
    }
  };

  const handleCustomRange = (vals: [Dayjs | null, Dayjs | null] | null) => {
    const range = vals && vals[0] && vals[1] ? ([vals[0], vals[1]] as [Dayjs, Dayjs]) : null;
    setCustomRange(range);
    const meterId = searchMode === 'meter' ? committedMeterId : null;
    const vendorName = searchMode === 'vendor' ? committedVendorName : null;
    emit('custom', range, meterId, vendorName);
  };

  // Switching Meter/Vendor resets both search inputs so a stale committed filter
  // from the other mode can't linger in the query.
  const handleSearchModeChange = (mode: AuditSearchMode) => {
    setSearchMode(mode);
    setMeterInput('');
    setResolvedMeter(null);
    setMeterNotFound(false);
    setCommittedMeterId(null);
    setVendorInput('');
    setCommittedVendorName(null);
    emit(preset, customRange, null, null);
  };

  // Validate on blur / tab-out
  const handleMeterBlur = () => {
    const trimmed = meterInput.trim();
    if (!trimmed) return;
    lookupMeter({ variables: { meterNumber: trimmed } });
  };

  // While typing: clear previous validation and committed meter so stale results don't linger
  const handleMeterInputChange = (value: string) => {
    setMeterInput(value);
    setResolvedMeter(null);
    setMeterNotFound(false);
    setCommittedMeterId(null);
    // suppress the general query while the user has text pending; clear it when input is empty
    emit(preset, customRange, null, null, !!value.trim());
  };

  const handleMeterSearch = () => {
    const nextMeterId = resolvedMeter?.meterId ?? null;
    setCommittedMeterId(nextMeterId);
    emit(preset, customRange, nextMeterId, null);
  };

  // Vendor name supports partial matching, so there's no resolve-before-search
  // step — the Search button simply commits whatever text is entered.
  const handleVendorInputChange = (value: string) => {
    setVendorInput(value);
    setCommittedVendorName(null);
    emit(preset, customRange, null, null, !!value.trim());
  };

  const handleVendorSearch = () => {
    const trimmed = vendorInput.trim();
    const nextVendorName = trimmed || null;
    setCommittedVendorName(nextVendorName);
    emit(preset, customRange, null, nextVendorName);
  };

  return (
    <div className={styles.filterPanel} data-testid="audit-filter-panel" data-search-mode={searchMode} data-preset={preset}>
      <div className={styles.filterRow}>
        <div className={styles.meterGroup}>
          <span className={styles.meterSearchLabel}>Search by:</span>
          <Select
            size="small"
            value={searchMode}
            onChange={handleSearchModeChange}
            options={SEARCH_MODE_OPTIONS}
            className={styles.searchModeSelect}
            data-testid="search-mode-select"
          />

          {searchMode === 'meter' ? (
            <>
              <Input
                size="small"
                placeholder="Meter number"
                data-testid="meter-number-input"
                value={meterInput}
                onChange={(e) => handleMeterInputChange(e.target.value)}
                onBlur={handleMeterBlur}
                allowClear
                onPressEnter={resolvedMeter ? handleMeterSearch : undefined}
                className={styles.meterInput}
              />
              <span className={styles.meterConfirmLabel}>Location / Meter Name:</span>
              {meterLoading ? (
                <span style={{ color: '#888', fontSize: 12 }} data-testid="meter-lookup-status" data-status="loading">Looking up…</span>
              ) : meterNotFound ? (
                <span style={{ color: '#ff4d4f', fontSize: 12 }} data-testid="meter-lookup-status" data-status="not-found">This meter number doesn&apos;t exist.</span>
              ) : resolvedMeter ? (
                <span style={{ color: '#389e0d', fontWeight: 500, fontSize: 12 }} data-testid="meter-lookup-status" data-status="found" data-meter-id={String(resolvedMeter.meterId)}>{resolvedMeter.meterName || '—'}</span>
              ) : null}
              <Button
                size="small"
                type="primary"
                icon={<SearchOutlined />}
                disabled={!resolvedMeter}
                onClick={handleMeterSearch}
                data-testid="meter-search-button"
              >
                Search
              </Button>
            </>
          ) : (
            <>
              <Input
                size="small"
                placeholder="Vendor name"
                data-testid="vendor-name-input"
                value={vendorInput}
                onChange={(e) => handleVendorInputChange(e.target.value)}
                allowClear
                onPressEnter={vendorInput.trim() ? handleVendorSearch : undefined}
                className={styles.meterInput}
              />
              <Button
                size="small"
                type="primary"
                icon={<SearchOutlined />}
                disabled={!vendorInput.trim()}
                onClick={handleVendorSearch}
                data-testid="vendor-search-button"
              >
                Search
              </Button>
            </>
          )}
        </div>

        <div className={styles.presetGroup}>
          {PRESET_LABELS.map(({ key, label }) => (
            <Tooltip key={key} title={presetTooltip(key)}>
              <Button
                type="text"
                size="small"
                onClick={() => handlePreset(key)}
                className={`${styles.presetBtn}${preset === key ? ` ${styles.presetBtnActive}` : ''}`}
                data-testid="date-preset-button"
                data-preset={key}
                data-active={String(preset === key)}
              >
                {label}
              </Button>
            </Tooltip>
          ))}
          {showRangePicker && (
            <div className={styles.rangePickerWrapper} data-testid="custom-range-picker">
              <RangePicker
                size="small"
                value={customRange}
                onChange={handleCustomRange}
                disabledDate={(d) => d.isAfter(dayjs())}
              />
            </div>
          )}
        </div>

        <span className={styles.recordCount} data-testid="record-count" data-count={String(recordCount)}>
          {recordCount} record{recordCount !== 1 ? 's' : ''} displayed
        </span>
      </div>
    </div>
  );
};

export default AuditFilterPanel;
